import { useState, useCallback, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface DaySchedule {
  readonly open: boolean;
  readonly start: string;
  readonly end: string;
}

const DAYS = [
  { key: "sun", label: "\u05D9\u05D5\u05DD \u05D0\u05F3" },
  { key: "mon", label: "\u05D9\u05D5\u05DD \u05D1\u05F3" },
  { key: "tue", label: "\u05D9\u05D5\u05DD \u05D2\u05F3" },
  { key: "wed", label: "\u05D9\u05D5\u05DD \u05D3\u05F3" },
  { key: "thu", label: "\u05D9\u05D5\u05DD \u05D4\u05F3" },
  { key: "fri", label: "\u05D9\u05D5\u05DD \u05D5\u05F3" },
  { key: "sat", label: "\u05E9\u05D1\u05EA" },
] as const;

function getIsraeliDefaults(): Record<string, DaySchedule> {
  return {
    sun: { open: true, start: "09:00", end: "18:00" },
    mon: { open: true, start: "09:00", end: "18:00" },
    tue: { open: true, start: "09:00", end: "18:00" },
    wed: { open: true, start: "09:00", end: "18:00" },
    thu: { open: true, start: "09:00", end: "18:00" },
    fri: { open: true, start: "08:00", end: "14:00" },
    sat: { open: false, start: "09:00", end: "18:00" },
  };
}

interface WorkHoursStepProps {
  readonly onComplete: (schedule: Record<string, DaySchedule>) => void;
}

export function WorkHoursStep({ onComplete }: WorkHoursStepProps): ReactNode {
  const [schedule, setSchedule] = useState<Record<string, DaySchedule>>(getIsraeliDefaults);

  const toggleDay = useCallback((dayKey: string) => {
    setSchedule((prev) => {
      const day = prev[dayKey];
      if (day === undefined) return prev;
      return { ...prev, [dayKey]: { ...day, open: !day.open } };
    });
  }, []);

  const updateTime = useCallback((dayKey: string, field: "start" | "end", value: string) => {
    setSchedule((prev) => {
      const day = prev[dayKey];
      if (day === undefined) return prev;
      return { ...prev, [dayKey]: { ...day, [field]: value } };
    });
  }, []);

  const resetDefaults = useCallback(() => {
    setSchedule(getIsraeliDefaults());
  }, []);

  return (
    <div className="flex flex-col gap-6 px-4 py-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">{"\u05E9\u05E2\u05D5\u05EA \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA"}</h2>
        <Button variant="ghost" size="sm" className="min-h-[44px] text-sm" onClick={resetDefaults}>
          {"\u05D0\u05E4\u05E1 \u05D1\u05E8\u05D9\u05E8\u05EA \u05DE\u05D7\u05D3\u05DC"}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        {"\u05E9\u05D1\u05EA \u05D5\u05D7\u05D2\u05D9\u05DD \u05E0\u05D7\u05E1\u05DE\u05D9\u05DD \u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9\u05EA"}
      </p>

      <div className="flex flex-col gap-2">
        {DAYS.map(({ key, label }) => {
          const day = schedule[key];
          if (day === undefined) return null;
          return (
            <Card key={key}>
              <CardContent className="flex items-center gap-3 p-3">
                <button
                  type="button"
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center"
                  onClick={() => toggleDay(key)}
                >
                  <div className={`h-5 w-5 rounded border ${
                    day.open ? "border-primary bg-primary" : "border-muted-foreground/40"
                  }`}>
                    {day.open && (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </div>
                </button>

                <span className="w-16 text-sm font-medium">{label}</span>

                {day.open ? (
                  <div className="flex flex-1 items-center gap-1">
                    <input
                      type="time"
                      value={day.start}
                      onChange={(e) => updateTime(key, "start", e.target.value)}
                      className="min-h-[44px] rounded border bg-background px-2 text-sm"
                      dir="ltr"
                    />
                    <span className="text-sm text-muted-foreground">-</span>
                    <input
                      type="time"
                      value={day.end}
                      onChange={(e) => updateTime(key, "end", e.target.value)}
                      className="min-h-[44px] rounded border bg-background px-2 text-sm"
                      dir="ltr"
                    />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">{"\u05E1\u05D2\u05D5\u05E8"}</span>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Button size="lg" className="min-h-[44px]" onClick={() => onComplete(schedule)}>
        {"\u05D4\u05DE\u05E9\u05DA"}
      </Button>
    </div>
  );
}
